import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import StatsTable, { TableColumn, TableRow, sharedScrollbarStyles } from '../common/StatsTable';

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  
  @media (max-width: 30rem) {
    justify-content: center;
  }
`;

const ScrollWrapper = styled.div`
  position: relative;
  max-height: clamp(320px, 45vh, 540px);
  overflow: hidden;
  border-radius: 12px;
  background: var(--color-surface);
`;

const ScrollInner = styled.div`
  overflow: auto;
  max-height: inherit;
  ${sharedScrollbarStyles};

  .stats-table-scroll-content {
    width: max-content;
  }
`;

const ToggleControl = styled.div`
  display: inline-flex;
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: 0.15rem;
`;

const ToggleOption = styled.button<{ $active?: boolean }>`
  padding: 0.35rem 0.85rem;
  border: none;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active }) => ($active ? 'var(--color-accent)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-secondary)')};

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--color-accent-light)' : 'var(--color-surface)')};
    color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-primary)')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

interface TeamStatsTableProps {
  statsType: 'traditional' | 'advanced' | 'insights';
  teamStatsData?: any[]; // Array format (like InsightsTeamStatsTable)
  opponentTeamStats?: any; // Single object format (legacy)
  previousOpponentStats?: any;
  opponentTeam?: any;
  currentSeason?: string;
  currentSeasonType?: string;
  loading?: boolean;
  error?: string | null;
}

export const TeamStatsTable: React.FC<TeamStatsTableProps> = ({
  statsType,
  teamStatsData,
  opponentTeamStats,
  previousOpponentStats,
  opponentTeam,
  currentSeason,
  currentSeasonType,
  loading = false,
  error = null
}) => {
  const [activeView, setActiveView] = useState<'averages' | 'totals' | 'advanced'>('averages');
  
  // Support both array format (teamStatsData) and single object format (opponentTeamStats)
  const isArrayFormat = Array.isArray(teamStatsData) && teamStatsData.length > 0;

  // Utility function to safely format stat values
  const formatStatValue = (value: any): string => {
    if (value === undefined || value === null) {
      return '-';
    }
    
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) {
      return '-';
    }
    
    return numValue.toFixed(1);
  };

  // Calculate percentage helper
  const calculatePercentage = (made: any, attempted: any): string => {
    if (made === undefined || made === null || attempted === undefined || attempted === null) {
      return '-';
    }
    try {
      const madeVal = typeof made === 'number' ? made : parseFloat(made);
      const attemptedVal = typeof attempted === 'number' ? attempted : parseFloat(attempted);
      if (isNaN(madeVal) || isNaN(attemptedVal) || attemptedVal === 0) {
        return '0.0';
      }
      return ((madeVal / attemptedVal) * 100).toFixed(1);
    } catch (e) {
      return '-';
    }
  };

  // Helper to get stat value from team data structure
  const getStatValue = (team: any, statKeys: string[]): any => {
    for (const key of statKeys) {
      const pathParts = key.split('.');
      let value: any = team;

      for (const part of pathParts) {
        value = value?.[part];
        if (value === undefined || value === null) break;
      }

      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return undefined;
  };

  type ColumnDef = {
    key: string;
    label: string;
    isFixed?: boolean;
    getter: (team: any) => string;
  };



  // All hooks must be called before any early returns
  // Check which views have data - support both array and single object formats
  const hasAveragesData = useMemo(() => {
    if (isArrayFormat) {
      return teamStatsData.some(row => {
        const stats = row.own?.average || row.own?.avg;
        return stats && Object.keys(stats).length > 0;
      });
    }
    const stats = opponentTeamStats || previousOpponentStats;
    return stats?.own?.average && Object.keys(stats.own.average).length > 0;
  }, [isArrayFormat, teamStatsData, opponentTeamStats, previousOpponentStats]);

  const hasTotalsData = useMemo(() => {
    if (isArrayFormat) {
      return teamStatsData.some(row => {
        const stats = row.own?.total;
        return stats && Object.keys(stats).length > 0;
      });
    }
    const stats = opponentTeamStats || previousOpponentStats;
    return stats?.own?.total && Object.keys(stats.own.total).length > 0;
  }, [isArrayFormat, teamStatsData, opponentTeamStats, previousOpponentStats]);

  const hasAdvancedData = useMemo(() => {
    if (isArrayFormat) {
      return teamStatsData.some(row => {
        const stats = row.own?.advanced;
        return stats && Object.keys(stats).length > 0;
      });
    }
    const stats = opponentTeamStats || previousOpponentStats;
    return stats?.own?.advanced && Object.keys(stats.own.advanced).length > 0;
  }, [isArrayFormat, teamStatsData, opponentTeamStats, previousOpponentStats]);

  // Auto-select view if current view has no data
  const currentView = useMemo(() => {
    if (activeView === 'averages' && hasAveragesData) return 'averages';
    if (activeView === 'totals' && hasTotalsData) return 'totals';
    if (activeView === 'advanced' && hasAdvancedData) return 'advanced';
    // Fallback to first available view
    if (hasAveragesData) return 'averages';
    if (hasTotalsData) return 'totals';
    if (hasAdvancedData) return 'advanced';
    return 'averages';
  }, [activeView, hasAveragesData, hasTotalsData, hasAdvancedData]);


  // Early returns after all hooks

  // Column definitions for averages view
  const averagesColumnDefs: ColumnDef[] = useMemo(() => {
    const base: ColumnDef[] = [
      // { key: 'team', label: 'TEAM', isFixed: true, getter: (team: any) => team.teamName || team.team || 'Unknown Team' }
    ];
    if (isArrayFormat) {
      base.push({ key: 'split', label: 'SPLIT', isFixed: true, getter: (team: any) => team.split || team.splitKey || 'overall' });
    }
    return [
      ...base,
    { key: 'wins', label: 'W', getter: (team: any) => formatStatValue(team.wins) },
    { key: 'losses', label: 'L', getter: (team: any) => formatStatValue(team.losses) },
    { key: 'pts', label: 'PTS', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.points', 'own.avg.points'])) },
    { key: 'fgm', label: 'FGM', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.field_goals_made', 'own.avg.field_goals_made'])) },
    { key: 'fga', label: 'FGA', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.field_goals_attempted', 'own.avg.field_goals_attempted'])) },
    { key: 'fgPct', label: 'FG%', getter: (team: any) => calculatePercentage(getStatValue(team, ['own.average.field_goals_made', 'own.avg.field_goals_made']), getStatValue(team, ['own.average.field_goals_attempted', 'own.avg.field_goals_attempted'])) },
    { key: 'threePm', label: '3PM', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.three_pointers_made', 'own.avg.three_pointers_made'])) },
    { key: 'threePa', label: '3PA', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.three_pointers_attempted', 'own.avg.three_pointers_attempted'])) },
    { key: 'threePct', label: '3P%', getter: (team: any) => calculatePercentage(getStatValue(team, ['own.average.three_pointers_made', 'own.avg.three_pointers_made']), getStatValue(team, ['own.average.three_pointers_attempted', 'own.avg.three_pointers_attempted'])) },
    { key: 'ftm', label: 'FTM', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.free_throws_made', 'own.avg.free_throws_made'])) },
    { key: 'fta', label: 'FTA', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.free_throws_attempted', 'own.avg.free_throws_attempted'])) },
    { key: 'ftPct', label: 'FT%', getter: (team: any) => calculatePercentage(getStatValue(team, ['own.average.free_throws_made', 'own.avg.free_throws_made']), getStatValue(team, ['own.average.free_throws_attempted', 'own.avg.free_throws_attempted'])) },
    { key: 'oreb', label: 'OREB', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.offensive_rebounds', 'own.avg.offensive_rebounds'])) },
    { key: 'dreb', label: 'DREB', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.defensive_rebounds', 'own.avg.defensive_rebounds'])) },
    { key: 'reb', label: 'REB', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.rebounds', 'own.avg.rebounds', 'own.average.total_rebounds', 'own.avg.total_rebounds'])) },
    { key: 'ast', label: 'AST', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.assists', 'own.avg.assists'])) },
    { key: 'tov', label: 'TOV', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.total_turnovers', 'own.avg.total_turnovers'])) },
    { key: 'stl', label: 'STL', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.steals', 'own.avg.steals'])) },
    { key: 'blk', label: 'BLK', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.blocks', 'own.avg.blocks'])) },
    { key: 'pf', label: 'PF', getter: (team: any) => formatStatValue(getStatValue(team, ['own.average.personal_fouls', 'own.avg.personal_fouls'])) }
    ];
  }, [isArrayFormat]);

  // Column definitions for totals view
  const totalsColumnDefs: ColumnDef[] = useMemo(() => {
    const base: ColumnDef[] = [
      { key: 'team', label: 'TEAM', isFixed: true, getter: (team: any) => team.teamName || team.team || 'Unknown Team' }
    ];
    if (isArrayFormat) {
      base.push({ key: 'split', label: 'SPLIT', getter: (team: any) => team.split || team.splitKey || 'overall' });
    }
    return [
      ...base,
    { key: 'wins', label: 'W', getter: (team: any) => formatStatValue(team.wins) },
    { key: 'losses', label: 'L', getter: (team: any) => formatStatValue(team.losses) },
    { key: 'pts', label: 'PTS', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.points'])) },
    { key: 'fgm', label: 'FGM', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.field_goals_made'])) },
    { key: 'fga', label: 'FGA', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.field_goals_attempted'])) },
    { key: 'fgPct', label: 'FG%', getter: (team: any) => calculatePercentage(getStatValue(team, ['own.total.field_goals_made']), getStatValue(team, ['own.total.field_goals_attempted'])) },
    { key: 'threePm', label: '3PM', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.three_pointers_made'])) },
    { key: 'threePa', label: '3PA', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.three_pointers_attempted'])) },
    { key: 'threePct', label: '3P%', getter: (team: any) => calculatePercentage(getStatValue(team, ['own.total.three_pointers_made']), getStatValue(team, ['own.total.three_pointers_attempted'])) },
    { key: 'ftm', label: 'FTM', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.free_throws_made'])) },
    { key: 'fta', label: 'FTA', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.free_throws_attempted'])) },
    { key: 'ftPct', label: 'FT%', getter: (team: any) => calculatePercentage(getStatValue(team, ['own.total.free_throws_made']), getStatValue(team, ['own.total.free_throws_attempted'])) },
    { key: 'oreb', label: 'OREB', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.offensive_rebounds'])) },
    { key: 'dreb', label: 'DREB', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.defensive_rebounds'])) },
    { key: 'reb', label: 'REB', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.rebounds', 'own.total.total_rebounds'])) },
    { key: 'ast', label: 'AST', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.assists'])) },
    { key: 'tov', label: 'TOV', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.turnovers'])) },
    { key: 'stl', label: 'STL', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.steals'])) },
    { key: 'blk', label: 'BLK', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.blocks'])) },
    { key: 'pf', label: 'PF', getter: (team: any) => formatStatValue(getStatValue(team, ['own.total.personal_fouls'])) }
    ];
  }, [isArrayFormat]);

  // Column definitions for advanced view
  const advancedColumnDefs: ColumnDef[] = useMemo(() => {
    const base: ColumnDef[] = [
      { key: 'team', label: 'TEAM', isFixed: true, getter: (team: any) => team.teamName || team.team || 'Unknown Team' }
    ];
    if (isArrayFormat) {
      base.push({ key: 'split', label: 'SPLIT', getter: (team: any) => team.split || team.splitKey || 'overall' });
    }
    return [
      ...base,
    { key: 'wins', label: 'W', getter: (team: any) => formatStatValue(team.wins) },
    { key: 'losses', label: 'L', getter: (team: any) => formatStatValue(team.losses) },
    { key: 'offRtg', label: 'OFF RTG', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.offensive_rating'])) },
    { key: 'defRtg', label: 'DEF RTG', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.defensive_rating'])) },
    { key: 'netRtg', label: 'NET RTG', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.net_rating'])) },
    { key: 'astPct', label: 'AST%', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.assist_percentage'])) },
    { key: 'astTo', label: 'AST/TO', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.assist_turnover_ratio'])) },
    { key: 'orebPct', label: 'OREB%', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.offensive_rebound_pct', 'own.advanced.offensive_rebound_percentage'])) },
    { key: 'drebPct', label: 'DREB%', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.defensive_rebound_pct', 'own.advanced.defensive_rebound_percentage'])) },
    { key: 'rebPct', label: 'REB%', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.total_rebound_pct', 'own.advanced.total_rebound_percentage'])) },
    { key: 'toRatio', label: 'TO RATIO', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.turnover_percentage'])) },
    { key: 'efgPct', label: 'EFG%', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.effective_field_goal_pct', 'own.shooting.effective_fg_pct', 'own.shooting.effective_field_goal_pct'])) },
    { key: 'tsPct', label: 'TS%', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.true_shooting_pct', 'own.shooting.true_shooting_pct', 'own.shooting.true_shooting_percentage'])) },
    { key: 'pace', label: 'PACE', getter: (team: any) => formatStatValue(getStatValue(team, ['own.advanced.pace'])) }
    ];
  }, [isArrayFormat]);

  // Select column definitions based on active view
  const columnDefs = useMemo(() => {
    if (currentView === 'averages') return averagesColumnDefs;
    if (currentView === 'totals') return totalsColumnDefs;
    return advancedColumnDefs;
  }, [currentView, averagesColumnDefs, totalsColumnDefs, advancedColumnDefs]);

  const columns: TableColumn[] = useMemo(() => {
    return columnDefs.map(({ key, label, isFixed }) => ({
      key,
      label,
      isFixed
    }));
  }, [columnDefs]);

  // Prepare data - build rows using column definitions
  const data: TableRow[] = useMemo(() => {
    if (isArrayFormat) {
      // Array format: map over teamStatsData using column definitions
      return teamStatsData.map((team) => {
        const row: TableRow = {};
        columnDefs.forEach(({ key, getter }) => {
          row[key] = getter(team);
        });
        return row;
      });
    } else {
      // Single object format: use opponentTeamStats (legacy format)
      const teamData = {
        ...opponentTeamStats,
        teamName: opponentTeamStats?.team_name || opponentTeam?.teamName || 'Loading...',
        team: opponentTeamStats?.team_name || opponentTeam?.teamName || 'Loading...'
      };
      
      const row: TableRow = {};
      columnDefs.forEach(({ key, getter }) => {
        row[key] = getter(teamData);
      });
      
      return [row];
    }
  }, [isArrayFormat, teamStatsData, opponentTeamStats, opponentTeam, columnDefs]);

  return (
    <TableSection>
      <ToggleRow>
        <ToggleControl>
          <ToggleOption
            type="button"
            onClick={() => setActiveView('averages')}
            $active={currentView === 'averages'}
            disabled={!hasAveragesData}
          >
            Averages
          </ToggleOption>
          {/* <ToggleOption
            type="button"
            onClick={() => setActiveView('totals')}
            $active={currentView === 'totals'}
            disabled={!hasTotalsData}
          >
            Totals
          </ToggleOption> */}
          <ToggleOption
            type="button"
            onClick={() => setActiveView('advanced')}
            $active={currentView === 'advanced'}
            disabled={!hasAdvancedData}
          >
            Advanced
          </ToggleOption>
        </ToggleControl>
      </ToggleRow>

      <ScrollWrapper>
        <ScrollInner>
          <div className="stats-table-scroll-content">
    <StatsTable
      columns={columns}
      data={data}
      stickyFirstColumn={true}
      stickyLeadingColumnCount={1}
              disableContainerScroll
    />
          </div>
        </ScrollInner>
      </ScrollWrapper>
    </TableSection>
  );
};
